import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type { Appointment, AppointmentFilters } from '../types/appointment';

async function fetchAppointments(
  clientId: string,
  accessToken: string | null | undefined,
  filters?: AppointmentFilters,
) {
  const params = new URLSearchParams();
  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.date_filter) {
    params.append('date_filter', filters.date_filter);
  }
  if (filters?.meeting_type) {
    params.append('meeting_type', filters.meeting_type);
  }

  const queryString = params.toString();
  const url = `/api/clients/${clientId}/appointments${queryString ? `?${queryString}` : ''}`;

  return apiRequest<Appointment[]>(url, { method: 'GET' }, accessToken);
}

export function useClientAppointments(
  clientId: string,
  filters?: AppointmentFilters,
) {
  const { session } = useAuth();

  return useQuery<Appointment[]>({
    queryKey: ['appointments', clientId, filters],
    enabled: Boolean(clientId && session),
    queryFn: () => fetchAppointments(clientId, session?.access_token, filters),
  });
}

type CreateAppointmentPayload = {
  title: string;
  notes?: string;
  starts_at: string;
  ends_at: string;
  meeting_type: 'in_person' | 'video' | 'phone';
  meeting_link?: string;
};

export function useCreateAppointment(clientId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateAppointmentPayload) => {
      return apiRequest<Appointment>(
        `/api/clients/${clientId}/appointments`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      // Invalidate all appointment queries for this client
      queryClient.invalidateQueries({
        queryKey: ['appointments', clientId],
      });
    },
  });
}

type UpdateAppointmentPayload = {
  title?: string;
  notes?: string;
  starts_at?: string;
  ends_at?: string;
  meeting_type?: 'in_person' | 'video' | 'phone';
  meeting_link?: string;
  status?: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  cancellation_reason?: string;
};

export function useUpdateAppointment(clientId: string, appointmentId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateAppointmentPayload) => {
      return apiRequest<Appointment>(
        `/api/clients/${clientId}/appointments/${appointmentId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      // Invalidate all appointment queries for this client
      queryClient.invalidateQueries({
        queryKey: ['appointments', clientId],
      });
    },
  });
}

export function useDeleteAppointment(clientId: string, appointmentId: string) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiRequest<{ success: boolean; message: string }>(
        `/api/clients/${clientId}/appointments/${appointmentId}`,
        {
          method: 'DELETE',
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      // Invalidate all appointment queries for this client
      queryClient.invalidateQueries({
        queryKey: ['appointments', clientId],
      });
    },
  });
}

export function useInvalidateAppointments(clientId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ['appointments', clientId],
    });
}
